import { useState } from 'react';
import difference from 'lodash/difference';
import BootstrapButtonGroup from 'react-bootstrap/ButtonGroup';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { numericStringCompare, truncateText } from 'common/util/string';
import { moveLeft, moveRight } from 'common/util/array';

import Button from 'components/common/Button/Button';
import SelectList from 'components/common/SelectList/SelectList';

import './Chooser.scss';

type ChooserProps = {
  sourceItems: Array<string>;
  targetItems: Array<string>;
  onSourceChange: (fields: Array<string>) => void;
  onTargetChange: (fields: Array<string>) => void;
  sourceTitle?: string;
  targetTitle?: string;
  sortTarget?: boolean;
  maxItemLength?: number;
  testId?: ELEMENT;
};

/**
 * This controlled component allows the user to choose items from a list. It does that by presenting two lists to the user, a list
 * of available items on the left, and a list of chosen items on the right, with buttons to shuffle items back and forth.
 *
 * @prop sourceItems       list of available items
 * @prop targetItems       list of chosen items
 * @prop onSourceChange    function to call when the list of source items changes
 * @prop onTargetChange    function to call when the list of target items changes
 * @prop sourceTitle       title for the list of available items
 * @prop targetTitle       title for the list of chosen items
 * @prop sortTarget        if true, maintain target list in sorted order
 * @prop maxItemLength     maximum number of characters to show for an item
 * @prop testId            unique string to use for testing
 */
function Chooser({
  sourceItems = [],
  targetItems = [],
  onSourceChange,
  onTargetChange,
  sourceTitle,
  targetTitle,
  sortTarget = true,
  maxItemLength,
  testId,
}: ChooserProps): JSX.Element {
  // for each list we track the full set and the selected set
  const [selectedSourceItems, setSelectedSourceItems] = useState<string[]>(K.EMPTY_ARRAY as string[]);
  const [selectedTargetItems, setSelectedTargetItems] = useState<string[]>(K.EMPTY_ARRAY as string[]);

  // button icons
  const addIcon = <ChevronRightIcon fontSize="small" />;
  const allIcon = (
    <>
      <ChevronRightIcon fontSize="small" />
      <ChevronRightIcon fontSize="small" className="Chooser__AllButtonIcon" />
    </>
  );
  const keyboardUpIcon = <KeyboardArrowUpIcon fontSize="small" />;
  const keyboardDownIcon = <KeyboardArrowDownIcon fontSize="small" />;
  const closeIcon = <CloseIcon fontSize="small" />;

  // button event handlers
  const doAdd = (): void => {
    let newTargetItems = targetItems.concat(selectedSourceItems);
    if (sortTarget) {
      newTargetItems.sort(numericStringCompare);
    }
    onTargetChange(newTargetItems);
    onSourceChange(difference(sourceItems, newTargetItems));
    setSelectedTargetItems([]);
    setSelectedSourceItems([]);
  };

  const doAddAll = (): void => {
    let newTargetItems = targetItems.concat(sourceItems);
    if (sortTarget) {
      newTargetItems.sort(numericStringCompare);
    }
    onTargetChange(newTargetItems);
    onSourceChange([]);
    setSelectedTargetItems([]);
    setSelectedSourceItems([]);
  };

  const doMoveUp = (): void => {
    const newTargetItems = moveLeft(targetItems, selectedTargetItems[0]);
    onTargetChange(newTargetItems);
  };

  const doMoveDown = (): void => {
    const newTargetItems = moveRight(targetItems, selectedTargetItems[0]);
    onTargetChange(newTargetItems);
  };

  const doRemove = (): void => {
    const newTargetItems = difference(targetItems, selectedTargetItems);
    onTargetChange(newTargetItems);
    onSourceChange(sourceItems.concat(selectedTargetItems).sort(numericStringCompare));
    setSelectedSourceItems([]);
    setSelectedTargetItems([]);
  };

  // if max item length has been provided, we need to specify both options and values
  let currentSourceOptions = sourceItems;
  let currentSourceValues;
  let currentTargetOptions = targetItems;
  let currentTargetValues;
  if (maxItemLength) {
    currentSourceOptions = sourceItems.map((item) => truncateText(item, maxItemLength));
    currentSourceValues = sourceItems;
    currentTargetOptions = targetItems.map((item) => truncateText(item, maxItemLength));
    currentTargetValues = targetItems;
  }

  let canMoveUp = false;
  let canMoveDown = false;
  if (selectedTargetItems.length === 1) {
    const pos = targetItems.indexOf(selectedTargetItems[0]);
    canMoveUp = pos > 0;
    canMoveDown = pos < targetItems.length - 1;
  }

  return (
    <div className="Chooser">
      <div className="Chooser__Source">
        <div className="Chooser__Header">{sourceTitle}</div>
        <SelectList
          multiple
          options={currentSourceOptions}
          values={currentSourceValues}
          selected={selectedSourceItems}
          className="Chooser__Select"
          onChange={(values: Array<string>): void => setSelectedSourceItems(values.slice())}
          testId={testId ? createTestId(testId, 'source') : undefined}
        />
      </div>
      <div className="Chooser__ButtonColumn">
        <div>
          <Button
            size="sm"
            rightIcon={addIcon}
            onClick={doAdd}
            className="Chooser__CenterButton"
            disabled={selectedSourceItems.length === 0}
            testId={testId ? createTestId(testId, 'add') : undefined}
          >
            Add
          </Button>
        </div>
        <div>
          <Button
            size="sm"
            rightIcon={allIcon}
            onClick={doAddAll}
            className="Chooser__AllButton Chooser__CenterButton"
            disabled={sourceItems.length === 0}
            testId={testId ? createTestId(testId, 'add-all') : undefined}
          >
            All
          </Button>
        </div>
      </div>
      <div className="Chooser__Target">
        <div className="Chooser__Header">{targetTitle}</div>
        <SelectList
          multiple
          options={currentTargetOptions}
          values={currentTargetValues}
          selected={selectedTargetItems}
          className="Chooser__Select"
          onChange={(values: Array<string>): void => setSelectedTargetItems(values.slice())}
          testId={testId ? createTestId(testId, 'target') : undefined}
        />
        <BootstrapButtonGroup className="Chooser__ButtonRow">
          <Button
            size="sm"
            leftIcon={keyboardUpIcon}
            onClick={doMoveUp}
            disabled={!canMoveUp}
            testId={testId ? createTestId(testId, 'move-up') : undefined}
          >
            Up
          </Button>
          <Button
            size="sm"
            leftIcon={keyboardDownIcon}
            onClick={doMoveDown}
            disabled={!canMoveDown}
            testId={testId ? createTestId(testId, 'move-down') : undefined}
          >
            Down
          </Button>
          <Button
            size="sm"
            leftIcon={closeIcon}
            onClick={doRemove}
            disabled={selectedTargetItems.length === 0}
            testId={testId ? createTestId(testId, 'remove') : undefined}
          >
            Remove
          </Button>
        </BootstrapButtonGroup>
      </div>
    </div>
  );
}

Chooser.defaultProps = {
  sourceTitle: '',
  targetTitle: '',
};

export default Chooser;
